import React, { useState, useMemo } from 'react';
import {
  StudentDemandRecord,
  StudentDemandFilter,
  DemandPlanningStatus,
} from '../../../types';
import {
  ALEXANDRIA_AREA_GROUPS,
  WEEKDAY_OPTIONS,
  getWeekdayLabel,
} from '../../../constants/alexandriaAreas';
import { SEED_UNIVERSITIES } from '../../../constants/mockSeed';
import {
  Search,
  Download,
  Phone,
  MessageSquare,
  Edit2,
  Archive,
  Eye,
  RefreshCw,
  Plus,
  Printer,
  Grid,
  Table as TableIcon,
  Users,
  Calendar,
  MapPin,
  GraduationCap,
  Copy,
  X,
  Trash2,
  Lock,
  Compass,
  Ticket,
} from 'lucide-react';

interface DemandDataTableProps {
  records: StudentDemandRecord[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  isLoading: boolean;
  filter: StudentDemandFilter;
  onFilterChange: (newFilter: StudentDemandFilter) => void;
  onPageChange: (page: number) => void;
  onEditRecord: (record: StudentDemandRecord) => void;
  onArchiveRecord: (recordId: string, studentName?: string) => void;
  onDeleteRecord?: (recordId: string, studentName: string) => void;
  onLockRoster?: () => void;
  onExportCsv: () => void;
  onRefresh: () => void;
  onOpenDataEntry?: () => void;
}

export const DemandDataTable: React.FC<DemandDataTableProps> = ({
  records,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  isLoading,
  filter,
  onFilterChange,
  onPageChange,
  onEditRecord,
  onArchiveRecord,
  onDeleteRecord,
  onLockRoster,
  onExportCsv,
  onRefresh,
  onOpenDataEntry,
}) => {
  const [viewMode, setViewMode] = useState<'TABLE' | 'CARDS'>('TABLE');
  const [selectedRecordForView, setSelectedRecordForView] = useState<StudentDemandRecord | null>(
    null
  );
  const [copiedPhone, setCopiedPhone] = useState<boolean>(false);

  // Extract all distinct universities (including custom user-added universities)
  const availableUniversities = useMemo(() => {
    const map = new Map<string, string>();
    SEED_UNIVERSITIES.forEach((u) => map.set(u.id, u.name));
    records.forEach((r) => {
      if (r.universityId && r.universityNameSnapshot && !map.has(r.universityId)) {
        map.set(r.universityId, r.universityNameSnapshot);
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ar'));
  }, [records]);

  // Extract all distinct assigned routes from loaded records
  const availableRoutes = useMemo(() => {
    const set = new Set<string>();
    records.forEach((r) => {
      if (r.assignedRoute && r.assignedRoute !== 'غير محدد') {
        set.add(r.assignedRoute);
      }
    });
    return Array.from(set).sort();
  }, [records]);

  // Extract all distinct pickup stations from loaded records
  const availablePickupStations = useMemo(() => {
    const set = new Set<string>();
    records.forEach((r) => {
      if (r.pickupStation && r.pickupStation !== 'غير محدد') {
        set.add(r.pickupStation);
      }
    });
    return Array.from(set).sort();
  }, [records]);

  // Statistics on current loaded records
  const stats = useMemo(() => {
    const active = records.filter((r) => r.status === 'ACTIVE').length;
    const withDays = records.filter(
      (r) => r.preferredTravelDays && r.preferredTravelDays.length > 0
    ).length;
    return { active, withDays };
  }, [records]);

  const handleFilterUpdate = (patch: Partial<StudentDemandFilter>) => {
    onFilterChange({ ...filter, ...patch });
    onPageChange(1); // Reset to page 1 on filter change
  };

  const handleResetFilters = () => {
    onFilterChange({
      search: '',
      universityId: 'ALL',
      areaGroupId: 'ALL',
      attendanceRange: 'ALL',
      travelDay: 'ALL',
      assignedRoute: 'ALL',
      pickupStation: 'ALL',
      status: 'ALL',
      planningStatus: 'ALL',
    });
    onPageChange(1);
  };


  const handleCopyPhone = (phone: string) => {
    if (!phone) return;
    navigator.clipboard?.writeText(phone);
    setCopiedPhone(true);
    setTimeout(() => setCopiedPhone(false), 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return { text: 'نشط', class: 'active' };
      case 'ARCHIVED':
        return { text: 'مؤرشف', class: 'archived' };
      default:
        return { text: status, class: 'archived' };
    }
  };

  const getPlanningLabel = (planning: DemandPlanningStatus) => {
    switch (planning) {
      case 'ROUTE_PLANNING':
        return { text: 'تخطيط خط', class: 'route_planning' };
      case 'HIGH_DEMAND':
        return { text: 'طلب مرتفع', class: 'high_demand' };
      case 'ROUTE_CREATED':
        return { text: 'تمت الجدولة', class: 'active' };
      case 'MONITORING':
        return { text: 'قيد المراقبة', class: 'monitoring' };
      case 'NEW':
      default:
        return { text: 'جديد', class: 'new' };
    }
  };

  return (
    <div className="demand-roster-wrapper space-y-4">
      {/* 1. Header Banner & Registry Actions */}
      <div className="demand-roster-header">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-2 rounded-xl bg-blue-50 text-blue-700 border border-blue-200">
                <Users size={20} />
              </span>
              <div>
                <h2 className="text-lg font-bold text-slate-900 m-0 flex items-center gap-2">
                  <span>سجل بيانات الطلاب ورغبات النقل</span>
                  <span className="text-xs font-mono font-bold bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded-full">
                    {totalCount.toLocaleString('ar-EG')} طالب
                  </span>
                </h2>
                <p className="text-xs text-slate-500 m-0 mt-0.5">
                  قاعدة بيانات الطلاب المسجلين بالمنظومة، رغبات السفر للجامعات، وسائل التواصل، ومواعيد الحضور.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-wrap no-print">
            {/* View Switcher */}
            <div className="bg-slate-100 p-1 rounded-xl flex items-center gap-1 border border-slate-200">
              <button
                type="button"
                className={`px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${
                  viewMode === 'TABLE'
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                onClick={() => setViewMode('TABLE')}
                title="عرض جدول البيانات المفصل"
              >
                <TableIcon size={14} />
                <span>جدول</span>
              </button>
              <button
                type="button"
                className={`px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${
                  viewMode === 'CARDS'
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                onClick={() => setViewMode('CARDS')}
                title="عرض بطاقات الطلاب التفاعلية"
              >
                <Grid size={14} />
                <span>بطاقات</span>
              </button>
            </div>

            {/* Print Roster */}
            <button
              type="button"
              className="btn-admin-outline text-xs py-2 px-3 flex items-center gap-1.5"
              onClick={handlePrint}
              title="طباعة كشف الطلاب"
            >
              <Printer size={14} />
              <span>طباعة الكشف</span>
            </button>

            {/* Export CSV */}
            <button
              type="button"
              className="btn-admin-outline text-xs py-2 px-3 flex items-center gap-1.5"
              onClick={onExportCsv}
              title="تصدير جدول الطلبات إلى ملف Excel / CSV"
            >
              <Download size={14} />
              <span>تصدير CSV</span>
            </button>

            {/* Refresh */}
            <button
              type="button"
              className="btn-admin-outline text-xs py-2 px-3 flex items-center gap-1.5"
              onClick={onRefresh}
              title="تحديث البيانات"
            >
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
              <span>تحديث</span>
            </button>

            {/* Lock Roster Button */}
            {onLockRoster && (
              <button
                type="button"
                className="btn-admin-outline text-xs py-2 px-3 flex items-center gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={onLockRoster}
                title="قفل سجل الطلاب وحمايته بكلمة مرور"
              >
                <Lock size={14} />
                <span>قفل السجل</span>
              </button>
            )}

            {/* Add New Student Data Entry */}
            {onOpenDataEntry && (
              <button
                type="button"
                className="btn-primary text-xs py-2 px-3 font-bold flex items-center gap-1.5 shadow-sm"
                onClick={onOpenDataEntry}
                title="تسجيل رغبة طالب جديد"
              >
                <Plus size={15} />
                <span>تسجيل طالب جديد</span>
              </button>
            )}
          </div>
        </div>

        {/* Quick Registry Metrics Pills */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-3 pt-3 border-t border-slate-100 text-xs">
          <div className="bg-slate-50 p-2 rounded-lg border border-slate-200 flex items-center justify-between">
            <span className="text-slate-600 font-medium">إجمالي المسجلين:</span>
            <strong className="font-mono text-slate-900 font-bold">
              {totalCount.toLocaleString('ar-EG')} طالب
            </strong>
          </div>
          <div className="bg-emerald-50 p-2 rounded-lg border border-emerald-200 flex items-center justify-between">
            <span className="text-emerald-700 font-medium">الطلاب النشطين:</span>
            <strong className="font-mono text-emerald-800 font-bold">
              {stats.active.toLocaleString('ar-EG')}
            </strong>
          </div>
          <div className="bg-blue-50 p-2 rounded-lg border border-blue-200 flex items-center justify-between">
            <span className="text-blue-700 font-medium">حددوا أيام أسبوعية:</span>
            <strong className="font-mono text-blue-800 font-bold">
              {stats.withDays.toLocaleString('ar-EG')}
            </strong>
          </div>
          <div className="bg-purple-50 p-2 rounded-lg border border-purple-200 flex items-center justify-between">
            <span className="text-purple-700 font-medium">الجامعات المسجلة:</span>
            <strong className="font-mono text-purple-800 font-bold">
              {availableUniversities.length.toLocaleString('ar-EG')} جامعة
            </strong>
          </div>
        </div>
      </div>

      {/* 2. Quick Weekdays Filter Bar */}
      <div className="demand-weekday-filter-bar no-print">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-slate-600 flex items-center gap-1">
            <Calendar size={13} className="text-blue-600" />
            <span>أيام السفر الأسبوعية:</span>
          </span>

          <button
            type="button"
            className={`weekday-filter-pill ${
              !filter.travelDay || filter.travelDay === 'ALL' ? 'active' : ''
            }`}
            onClick={() => handleFilterUpdate({ travelDay: 'ALL' })}
          >
            جميع الأيام
          </button>

          {WEEKDAY_OPTIONS.map((w) => {
            const isSelected = filter.travelDay === w.id;
            return (
              <button
                key={w.id}
                type="button"
                className={`weekday-filter-pill ${isSelected ? 'active' : ''}`}
                onClick={() => handleFilterUpdate({ travelDay: isSelected ? 'ALL' : w.id })}
              >
                <span>{w.labelAr}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. Search and Multi-Filters Bar */}
      <div className="demand-table-filter-bar no-print">
        {/* Search */}
        <div className="demand-search-input-wrap">
          <input
            type="text"
            placeholder="بحث بالاسم، الهاتف، الجامعة، أو المنطقة..."
            value={filter.search || ''}
            onChange={(e) => handleFilterUpdate({ search: e.target.value })}
          />
          <Search size={16} className="demand-search-icon" />
        </div>

        {/* University Filter */}
        <select
          value={filter.universityId || 'ALL'}
          onChange={(e) => handleFilterUpdate({ universityId: e.target.value })}
          className="demand-filter-select"
        >
          <option value="ALL">جميع الجامعات ({availableUniversities.length})</option>
          {availableUniversities.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>

        {/* Route Filter (خط السير المسجل) */}
        <select
          value={filter.assignedRoute || 'ALL'}
          onChange={(e) => handleFilterUpdate({ assignedRoute: e.target.value })}
          className="demand-filter-select"
        >
          <option value="ALL">جميع خطوط السير ({availableRoutes.length})</option>
          {availableRoutes.map((rt) => (
            <option key={rt} value={rt}>
              {rt}
            </option>
          ))}
        </select>

        {/* Pickup Station Filter (مكان الركوب) */}
        <select
          value={filter.pickupStation || 'ALL'}
          onChange={(e) => handleFilterUpdate({ pickupStation: e.target.value })}
          className="demand-filter-select"
        >
          <option value="ALL">جميع أماكن الركوب ({availablePickupStations.length})</option>
          {availablePickupStations.map((st) => (
            <option key={st} value={st}>
              {st}
            </option>
          ))}
        </select>

        {/* Area Group Filter */}
        <select
          value={filter.areaGroupId || 'ALL'}
          onChange={(e) => handleFilterUpdate({ areaGroupId: e.target.value })}
          className="demand-filter-select"
        >
          <option value="ALL">جميع القطاعات السكنية</option>
          {ALEXANDRIA_AREA_GROUPS.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>

        {/* Attendance Days Range Filter */}
        <select
          value={filter.attendanceRange || 'ALL'}
          onChange={(e) =>
            handleFilterUpdate({
              attendanceRange: e.target.value as any,
            })
          }
          className="demand-filter-select"
        >
          <option value="ALL">أيام الحضور شهرياً</option>
          <option value="1-5">1 إلى 5 أيام</option>
          <option value="6-10">6 إلى 10 أيام</option>
          <option value="11-15">11 إلى 15 يوماً</option>
          <option value="16-20">16 إلى 20 يوماً</option>
          <option value="21+">21 يوماً فأكثر</option>
        </select>

        {/* Planning Status Filter */}
        <select
          value={filter.planningStatus || 'ALL'}
          onChange={(e) => handleFilterUpdate({ planningStatus: e.target.value as any })}
          className="demand-filter-select"
        >
          <option value="ALL">جميع حالات التخطيط</option>
          <option value="NEW">جديد</option>
          <option value="MONITORING">قيد المراقبة</option>
          <option value="HIGH_DEMAND">طلب مرتفع</option>
          <option value="ROUTE_PLANNING">تخطيط خط</option>
          <option value="ROUTE_CREATED">تمت الجدولة</option>
        </select>

        {/* Status Filter */}
        <select
          value={filter.status || 'ALL'}
          onChange={(e) => handleFilterUpdate({ status: e.target.value as any })}
          className="demand-filter-select"
        >
          <option value="ALL">جميع الحالات</option>
          <option value="ACTIVE">النشطة فقط</option>
          <option value="ARCHIVED">المؤرشفة</option>
        </select>

        {/* Reset Filter Button */}
        {(filter.search ||
          (filter.universityId && filter.universityId !== 'ALL') ||
          (filter.assignedRoute && filter.assignedRoute !== 'ALL') ||
          (filter.pickupStation && filter.pickupStation !== 'ALL') ||
          (filter.areaGroupId && filter.areaGroupId !== 'ALL') ||
          (filter.attendanceRange && filter.attendanceRange !== 'ALL') ||
          (filter.travelDay && filter.travelDay !== 'ALL') ||
          (filter.planningStatus && filter.planningStatus !== 'ALL') ||
          (filter.status && filter.status !== 'ALL')) && (
          <button
            type="button"
            className="text-xs text-rose-600 hover:text-rose-800 font-bold px-2 py-1 flex items-center gap-1 bg-rose-50 rounded-lg border border-rose-200"
            onClick={handleResetFilters}
          >
            <X size={12} />
            <span>مسح الفلاتر</span>
          </button>
        )}
      </div>

      {/* 4. Main Content Area */}
      {isLoading ? (
        <div className="p-16 text-center text-slate-500 bg-white rounded-2xl border border-slate-200 flex flex-col items-center justify-center gap-3">
          <div className="loading-spinner"></div>
          <span className="text-sm font-bold text-slate-700">جارٍ تحميل سجل بيانات الطلاب...</span>
        </div>
      ) : records.length === 0 ? (
        <div className="p-16 text-center text-slate-500 bg-white rounded-2xl border border-slate-200">
          <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
            <Users size={28} />
          </div>
          <h3 className="font-bold text-slate-800 text-base mb-1">لا توجد بيانات طلاب مطابقة</h3>
          <p className="text-xs text-slate-500 max-w-md mx-auto mb-4">
            لم يتم العثور على سجلات تطابق الفلاتر المحددة، أو لم يتم تسجيل أي رغبات بعد. يمكنك إضافة رغبة طالب جديدة لبدء التشغيل.
          </p>
          {onOpenDataEntry && (
            <button
              type="button"
              className="btn-primary text-xs py-2 px-4 font-bold inline-flex items-center gap-1.5 shadow-sm"
              onClick={onOpenDataEntry}
            >
              <Plus size={15} />
              <span>تسجيل طالب جديد الآن</span>
            </button>
          )}
        </div>
      ) : viewMode === 'TABLE' ? (
        /* View 1: Detailed Table View */
        <div className="demand-table-container">
          <table className="demand-table">
            <thead>
              <tr>
                <th className="w-10">#</th>
                <th>اسم الطالب</th>
                <th>الهاتف والتواصل</th>
                <th>الجامعة والكلية</th>
                <th>خط السير المسجل</th>
                <th>مكان الركوب</th>
                <th>المنطقة السكنية</th>
                <th>أيام وتواريخ السفر</th>
                <th>المصدر</th>
                <th>الحالة</th>
                <th>تاريخ الإضافة</th>
                <th className="no-print">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, idx) => {
                const s = getStatusLabel(r.status);
                const p = getPlanningLabel(r.planningStatus);
                const cleanPhone = (r.phone || '').replace(/\D/g, '');
                const waPhone = cleanPhone.startsWith('0') ? '2' + cleanPhone : cleanPhone;
                const rowNum = (currentPage - 1) * pageSize + idx + 1;

                return (
                  <tr key={r.id}>
                    {/* Index */}
                    <td className="font-mono text-xs text-slate-400 text-center">{rowNum}</td>

                    {/* Student Name */}
                    <td>
                      <div>
                        <strong className="block text-slate-900 text-sm font-bold">
                          {r.studentName}
                        </strong>
                        {r.notes && (
                          <span
                            className="text-[11px] text-slate-400 block line-clamp-1 max-w-[180px]"
                            title={r.notes}
                          >
                            {r.notes}
                          </span>
                        )}
                        <span className="text-[10px] text-slate-400 font-mono">#{r.id.slice(-6)}</span>
                      </div>
                    </td>

                    {/* Phone & Contacts */}
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-slate-700 font-bold" dir="ltr">
                          {r.phone}
                        </span>

                        {cleanPhone && (
                          <div className="flex items-center gap-1 no-print">
                            <a
                              href={`https://wa.me/${waPhone}?text=${encodeURIComponent(
                                `مرحباً ${r.studentName}، بخصوص رغبتك بالانتقال لـ ${r.universityNameSnapshot} عبر خدمة طلاب OTO STUDENTS...`
                              )}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-emerald-600 hover:text-emerald-700 p-1 bg-emerald-50 rounded-md hover:bg-emerald-100 transition-colors"
                              title="محادثة واتساب مباشرة"
                            >
                              <MessageSquare size={13} />
                            </a>
                            <a
                              href={`tel:${cleanPhone}`}
                              className="text-blue-600 hover:text-blue-700 p-1 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                              title="اتصال هاتفي مباشر"
                            >
                              <Phone size={13} />
                            </a>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* University & College */}
                    <td>
                      <div>
                        <strong className="text-slate-900 text-xs font-bold block">
                          {r.universityNameSnapshot}
                        </strong>
                        {r.college && (
                          <span className="text-[11px] text-blue-700 font-medium block">
                            {r.college}
                          </span>
                        )}
                        <span className="text-[10px] text-slate-400 block">
                          {r.universityGovernorateNameSnapshot}
                        </span>
                      </div>
                    </td>

                    {/* Assigned Route (خط السير المسجل) */}
                    <td>
                      <div className="flex items-center gap-1.5 max-w-[200px]">
                        <Compass size={13} className="text-indigo-600 flex-shrink-0" />
                        <span className="text-xs font-bold text-slate-800 leading-tight block">
                          {r.assignedRoute || 'غير محدد بعد'}
                        </span>
                      </div>
                    </td>

                    {/* Pickup Point / Station (مكان الركوب) */}
                    <td>
                      <div className="flex items-center gap-1.5">
                        <MapPin size={13} className="text-emerald-600 flex-shrink-0" />
                        <strong className="text-xs text-slate-800 font-bold block">
                          {r.pickupStation || r.areaName}
                        </strong>
                      </div>
                    </td>

                    {/* Area & Sector */}
                    <td>
                      <div>
                        <strong className="text-slate-800 text-xs block">{r.areaName}</strong>
                        <span className="text-[11px] text-slate-500 block">{r.areaGroupName}</span>
                      </div>
                    </td>

                    {/* Travel Days of the Week & Specific Dates */}
                    <td>
                      <div className="space-y-1">
                        {r.preferredTravelDays && r.preferredTravelDays.length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-[150px]">
                            {r.preferredTravelDays.map((d) => (
                              <span
                                key={d}
                                className="weekday-badge active text-[10px] py-0 px-1.5 font-bold"
                              >
                                {getWeekdayLabel(d)}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 block">لم تحدد</span>
                        )}

                        {r.specificTravelDates && r.specificTravelDates.length > 0 && (
                          <span className="text-[10px] font-mono font-bold bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200 block">
                            {r.specificTravelDates.length} تواريخ محددة
                          </span>
                        )}

                        {r.attendanceDaysPerMonth && (
                          <span className="text-[10px] text-slate-500 font-mono block">
                            {r.attendanceDaysPerMonth} يوم / شهر
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Source Type (مصدر الطلب) */}
                    <td>
                      <div>
                        {r.sourceType === 'BOOKING' ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full">
                            <Ticket size={11} />
                            <span>حجز رحلات</span>
                          </span>
                        ) : r.sourceType === 'REGISTERED_STUDENT' ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-indigo-50 text-indigo-800 border border-indigo-200 px-2 py-0.5 rounded-full">
                            <Users size={11} />
                            <span>طالب مسجل</span>
                          </span>
                        ) : (
                          <span className="text-[11px] font-bold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
                            رغبة نقل
                          </span>
                        )}

                        {r.bookingCodes && r.bookingCodes.length > 0 && (
                          <span className="text-[10px] font-mono text-slate-500 block mt-0.5" title={r.bookingCodes.join(', ')}>
                            #{r.bookingCodes[0]}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Status */}
                    <td>
                      <div className="flex flex-col gap-1 items-start">
                        <span className={`demand-status-badge ${s.class}`}>{s.text}</span>
                        <span className={`demand-status-badge ${p.class}`}>{p.text}</span>
                      </div>
                    </td>

                    {/* Created Date */}
                    <td>
                      <span className="font-mono text-xs text-slate-500">
                        {r.createdAt ? r.createdAt.split('T')[0] : '—'}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="no-print">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          className="btn-admin-outline text-xs p-1.5 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                          onClick={() => setSelectedRecordForView(r)}
                          title="عرض بطاقة الطالب الكاملة"
                        >
                          <Eye size={13} />
                        </button>

                        <button
                          type="button"
                          className="btn-admin-outline text-xs p-1.5 hover:bg-slate-100 transition-colors"
                          onClick={() => onEditRecord(r)}
                          title="تعديل بيانات الطالب"
                        >
                          <Edit2 size={13} />
                        </button>

                        {r.status === 'ACTIVE' && (
                          <button
                            type="button"
                            className="btn-admin-outline text-xs p-1.5 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                            onClick={() => onArchiveRecord(r.id, r.studentName)}
                            title="أرشفة السجل"
                          >
                            <Archive size={13} />
                          </button>
                        )}

                        {onDeleteRecord && (
                          <button
                            type="button"
                            className="btn-admin-danger text-xs p-1.5"
                            onClick={() => onDeleteRecord(r.id, r.studentName)}
                            title="حذف الطلب نهائياً"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        /* View 2: Student Cards Grid View */
        <div className="student-cards-grid">
          {records.map((r) => {
            const s = getStatusLabel(r.status);
            const p = getPlanningLabel(r.planningStatus);
            const cleanPhone = (r.phone || '').replace(/\D/g, '');
            const waPhone = cleanPhone.startsWith('0') ? '2' + cleanPhone : cleanPhone;
            const initials = r.studentName.slice(0, 2);

            return (
              <div key={r.id} className="student-card">
                {/* Card Top: Avatar & Name */}
                <div className="student-card-header">
                  <div className="flex items-center gap-2.5">
                    <div className="student-avatar">{initials}</div>
                    <div>
                      <h4 className="student-name">{r.studentName}</h4>
                      <span className="student-id-code">#{r.id.slice(-6)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <span className={`demand-status-badge ${s.class}`}>{s.text}</span>
                    <span className={`demand-status-badge ${p.class}`}>{p.text}</span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="student-card-body">
                  {/* Phone & Instant Contact */}
                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="font-mono text-xs font-bold text-slate-800" dir="ltr">
                      {r.phone}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <a
                        href={`https://wa.me/${waPhone}?text=${encodeURIComponent(
                          `مرحباً ${r.studentName}، بخصوص رغبتك بالانتقال لـ ${r.universityNameSnapshot} عبر خدمة طلاب OTO STUDENTS...`
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                        className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded-lg text-xs font-bold flex items-center gap-1 hover:bg-emerald-200"
                        title="واتساب مباشر"
                      >
                        <MessageSquare size={12} />
                        <span>واتساب</span>
                      </a>
                      <a
                        href={`tel:${cleanPhone}`}
                        className="p-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200"
                        title="اتصال هاتفي"
                      >
                        <Phone size={12} />
                      </a>
                    </div>
                  </div>

                  {/* University & Destination */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-xs">
                      <GraduationCap size={13} className="text-blue-600 flex-shrink-0" />
                      <strong className="text-slate-900">{r.universityNameSnapshot}</strong>
                      {r.college && <span className="text-blue-700 font-medium">({r.college})</span>}
                    </div>

                    <div className="flex items-center gap-1.5 text-xs text-slate-600">
                      <MapPin size={13} className="text-emerald-600 flex-shrink-0" />
                      <span>
                        {r.areaName} — {r.areaGroupName}
                      </span>
                    </div>
                  </div>

                  {/* Route & Pickup Station Box */}
                  <div className="p-2 bg-slate-50 rounded-xl border border-slate-100 text-xs space-y-1">
                    <div className="flex items-center gap-1.5 text-slate-800">
                      <Compass size={12} className="text-indigo-600 flex-shrink-0" />
                      <span className="font-bold text-[11px] leading-tight">
                        {r.assignedRoute || 'غير محدد بعد'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-600 text-[11px] pt-1 border-t border-slate-200/50">
                      <MapPin size={12} className="text-emerald-600 flex-shrink-0" />
                      <span>مكان الركوب: <strong className="text-slate-800">{r.pickupStation || r.areaName}</strong></span>
                    </div>
                  </div>

                  {/* Schedule & Days */}
                  <div className="p-2 bg-blue-50/50 rounded-xl border border-blue-100 text-xs space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600 font-medium">الحضور شهرياً:</span>
                      <strong className="text-blue-900 font-mono">
                        {r.attendanceDaysPerMonth ? `${r.attendanceDaysPerMonth} يوم` : 'غير محدد'}
                      </strong>
                    </div>

                    {r.preferredTravelDays && r.preferredTravelDays.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-1 border-t border-blue-100/60">
                        {r.preferredTravelDays.map((d) => (
                          <span
                            key={d}
                            className="weekday-badge active text-[10px] py-0 px-1.5 font-bold"
                          >
                            {getWeekdayLabel(d)}
                          </span>
                        ))}
                      </div>
                    )}

                    {(r.preferredDepartureTime || r.preferredReturnTime) && (
                      <div className="flex items-center gap-2 text-[11px] text-slate-500 font-mono pt-1">
                        {r.preferredDepartureTime && <span>ذهاب: {r.preferredDepartureTime}</span>}
                        {r.preferredReturnTime && <span>عودة: {r.preferredReturnTime}</span>}
                      </div>
                    )}
                  </div>

                  {/* Notes snippet */}
                  {r.notes && (
                    <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded-lg line-clamp-2 m-0 border border-slate-100">
                      {r.notes}
                    </p>
                  )}
                </div>

                {/* Card Footer: Metadata & Actions */}
                <div className="student-card-footer">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-slate-400 font-mono">
                      {r.createdAt ? r.createdAt.split('T')[0] : ''}
                    </span>
                    {r.sourceType === 'BOOKING' && (
                      <span className="text-[9px] bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded font-bold">
                        حجز رحلات
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      className="btn-admin-outline text-xs px-2 py-1 flex items-center gap-1"
                      onClick={() => setSelectedRecordForView(r)}
                    >
                      <Eye size={12} />
                      <span>تفاصيل</span>
                    </button>
                    <button
                      type="button"
                      className="btn-admin-outline text-xs p-1"
                      onClick={() => onEditRecord(r)}
                      title="تعديل"
                    >
                      <Edit2 size={12} />
                    </button>
                    {r.status === 'ACTIVE' && (
                      <button
                        type="button"
                        className="btn-admin-outline text-xs p-1 hover:bg-amber-50 hover:text-amber-700"
                        onClick={() => onArchiveRecord(r.id, r.studentName)}
                        title="أرشفة"
                      >
                        <Archive size={12} />
                      </button>
                    )}
                    {onDeleteRecord && (
                      <button
                        type="button"
                        className="btn-admin-danger text-xs p-1"
                        onClick={() => onDeleteRecord(r.id, r.studentName)}
                        title="حذف نهائي (يتطلب كلمة المرور)"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 5. Pagination Bar */}
      <div className="demand-pagination-bar no-print">
        <div>
          <span>
            عرض السجلات من {((currentPage - 1) * pageSize + 1).toLocaleString('ar-EG')} إلى{' '}
            {Math.min(currentPage * pageSize, totalCount).toLocaleString('ar-EG')} من إجمالي{' '}
            {totalCount.toLocaleString('ar-EG')} طالب مسجل
          </span>
        </div>

        <div className="demand-pagination-btns">
          <button
            type="button"
            className="demand-page-btn"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            السابق
          </button>

          <span className="px-2 py-1 text-xs font-bold text-slate-700">
            صفحة {currentPage} من {totalPages || 1}
          </span>

          <button
            type="button"
            className="demand-page-btn"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          >
            التالي
          </button>
        </div>
      </div>

      {/* 6. Comprehensive Student Details Modal */}
      {selectedRecordForView && (
        <div
          className="demand-modal-overlay"
          onClick={() => setSelectedRecordForView(null)}
        >
          <div className="demand-modal-box max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="demand-modal-header">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">
                  {selectedRecordForView.studentName.slice(0, 2)}
                </div>
                <div>
                  <h3 className="demand-modal-title text-base font-bold text-slate-900 m-0">
                    {selectedRecordForView.studentName}
                  </h3>
                  <span className="text-xs text-slate-500 font-mono">
                    كود السجل: #{selectedRecordForView.id}
                  </span>
                </div>
              </div>

              <button
                className="demand-modal-close-btn"
                onClick={() => setSelectedRecordForView(null)}
              >
                ✕
              </button>
            </div>

            <div className="space-y-3.5 text-sm">
              {/* Direct Communication Action Box */}
              <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 flex items-center justify-between">
                <div>
                  <span className="text-xs text-blue-800 font-semibold block">رقم الهاتف المسجل:</span>
                  <strong className="font-mono text-base text-slate-900" dir="ltr">
                    {selectedRecordForView.phone}
                  </strong>
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    className="btn-admin-outline text-xs px-2.5 py-1.5 bg-white flex items-center gap-1"
                    onClick={() => handleCopyPhone(selectedRecordForView.phone)}
                  >
                    <Copy size={13} />
                    <span>{copiedPhone ? 'تم النسخ!' : 'نسخ'}</span>
                  </button>

                  <a
                    href={`https://wa.me/${(selectedRecordForView.phone || '').replace(/\D/g, '').startsWith('0') ? '2' + (selectedRecordForView.phone || '').replace(/\D/g, '') : (selectedRecordForView.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(
                      `مرحباً ${selectedRecordForView.studentName}، بخصوص رغبتك بالانتقال لـ ${selectedRecordForView.universityNameSnapshot} عبر خدمة طلاب OTO STUDENTS...`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-bold flex items-center gap-1 hover:bg-emerald-700 transition-colors shadow-sm"
                  >
                    <MessageSquare size={13} />
                    <span>واتساب</span>
                  </a>

                  <a
                    href={`tel:${selectedRecordForView.phone}`}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold flex items-center gap-1 hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    <Phone size={13} />
                    <span>اتصال</span>
                  </a>
                </div>
              </div>

              {/* Grid of Details */}
              <div className="grid grid-cols-2 gap-3 p-3.5 bg-slate-50 rounded-xl border border-slate-200">
                <div>
                  <span className="text-xs text-slate-500 block">الجامعة المطلوبة</span>
                  <strong className="text-slate-900 font-bold">
                    {selectedRecordForView.universityNameSnapshot}
                  </strong>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">الكلية / التخصص</span>
                  <strong className="text-slate-900 font-bold">
                    {selectedRecordForView.college || 'غير محدد'}
                  </strong>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">خط السير المسجل</span>
                  <strong className="text-indigo-800 font-bold flex items-center gap-1">
                    <Compass size={13} className="text-indigo-600 flex-shrink-0" />
                    <span>{selectedRecordForView.assignedRoute || 'غير محدد بعد'}</span>
                  </strong>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">مكان الركوب المحدد</span>
                  <strong className="text-emerald-800 font-bold flex items-center gap-1">
                    <MapPin size={13} className="text-emerald-600 flex-shrink-0" />
                    <span>{selectedRecordForView.pickupStation || selectedRecordForView.areaName}</span>
                  </strong>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">المنطقة السكنية والقطاع</span>
                  <strong className="text-slate-900 font-bold">
                    {selectedRecordForView.areaName} ({selectedRecordForView.areaGroupName})
                  </strong>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">أيام الحضور شهرياً</span>
                  <strong className="text-slate-900 font-mono font-bold">
                    {selectedRecordForView.attendanceDaysPerMonth
                      ? `${selectedRecordForView.attendanceDaysPerMonth} يوم / شهر`
                      : 'غير محدد'}
                  </strong>
                </div>

                {/* Specific Booked Dates if linked */}
                {selectedRecordForView.specificTravelDates && selectedRecordForView.specificTravelDates.length > 0 && (
                  <div className="col-span-2 pt-2 border-t border-slate-200/60">
                    <span className="text-xs text-slate-500 block mb-1 font-medium">
                      تواريخ السفر المحددة بالحجز:
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {selectedRecordForView.specificTravelDates.map((dt) => (
                        <span key={dt} className="text-[11px] font-mono font-bold bg-blue-100 text-blue-800 px-2 py-0.5 rounded-md">
                          {dt}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="col-span-2 pt-2 border-t border-slate-200/60">
                  <span className="text-xs text-slate-500 block mb-1 font-medium">
                    أيام السفر الأسبوعية المحددة:
                  </span>
                  {selectedRecordForView.preferredTravelDays &&
                  selectedRecordForView.preferredTravelDays.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedRecordForView.preferredTravelDays.map((d) => (
                        <span key={d} className="weekday-badge active font-bold">
                          {getWeekdayLabel(d)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">لم يتم تحديد أيام محددة</span>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedRecordForView.notes && (
                <div className="p-3 bg-white rounded-xl border border-slate-200">
                  <span className="text-xs font-bold text-slate-700 block mb-1">الملاحظات:</span>
                  <p className="text-xs text-slate-600 leading-relaxed m-0 whitespace-pre-wrap">
                    {selectedRecordForView.notes}
                  </p>
                </div>
              )}

              {/* Audit trail */}
              <div className="text-xs text-slate-400 space-y-1 pt-1 border-t border-slate-100">
                <div>تاريخ التسجيل: {selectedRecordForView.createdAt}</div>
                <div>تم التسجيل بواسطة: {selectedRecordForView.createdBy?.email}</div>
                <div>مصدر السجل: {selectedRecordForView.sourceType || 'رغبة نقل'}</div>
                <div>حالة التخطيط: {selectedRecordForView.planningStatus}</div>
              </div>
            </div>

            <div className="demand-modal-actions mt-4 flex items-center justify-between">
              <button
                type="button"
                className="btn-primary text-xs py-2 px-3 flex items-center gap-1.5"
                onClick={() => {
                  const rec = selectedRecordForView;
                  setSelectedRecordForView(null);
                  onEditRecord(rec);
                }}
              >
                <Edit2 size={13} />
                <span>تعديل بيانات الطالب</span>
              </button>

              <button
                type="button"
                className="btn-admin-outline text-xs py-2 px-4"
                onClick={() => setSelectedRecordForView(null)}
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
